/**
 * act-as-mutation-confirm.tsx — Reusable mutation confirmation dialog for act-as CDN operations.
 *
 * Every CDN MUTATE in A2/A3/A4 goes through this dialog. Shows a summary of the
 * pending change, requires the operator to type a non-empty reason, then fires the
 * mutation, toasts success/error, and invalidates the relevant query keys.
 *
 * Usage:
 *   <ActAsMutationConfirm
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Save cache configuration"
 *     summary="Update TTL and cache key strategy for example.com"
 *     queryKeysToInvalidate={[actAsCDNKeys.cache(domainId)]}
 *     mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/cache`, payload)}
 *     onSuccess={() => reset(form)}
 *   />
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '@vnetwork/ui/primitives';
import { Button, Input } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';

/* ── Props ──────────────────────────────────────────────────────────────── */

export interface ActAsMutationConfirmProps {
  /** Whether the dialog is open. */
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** Dialog heading — short verb phrase, e.g. "Save cache configuration". */
  title: string;

  /** Human-readable summary of what will change. Shown as pre-flight context. */
  summary: string;

  /**
   * Query cache keys to invalidate after a successful mutation.
   * Use the actAsCDNKeys factory from @/api/act-as-cdn(-cache).
   * Accepts both mutable and readonly tuple arrays (as returned by `as const` query-key factories).
   */
  queryKeysToInvalidate?: ReadonlyArray<readonly unknown[]>;

  /**
   * The actual async mutation to run when the operator confirms.
   * Should resolve on success and throw/reject on error.
   */
  mutationFn: () => Promise<unknown>;

  /** Called after the mutation succeeds and keys are invalidated. */
  onSuccess?: () => void;

  /** Labels for the confirm button — defaults to "Confirm". */
  confirmLabel?: string;

  /** Mark as destructive (red confirm button). */
  destructive?: boolean;
}

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * Mutation confirm dialog for act-as CDN operations.
 *
 * Forces the operator to supply a non-empty reason before confirming.
 * On confirm: runs mutationFn → invalidates queryKeysToInvalidate → toasts → calls onSuccess.
 * On error: toasts danger and keeps the dialog open so the operator can retry or cancel.
 */
export function ActAsMutationConfirm({
  open,
  onOpenChange,
  title,
  summary,
  queryKeysToInvalidate = [] as ReadonlyArray<readonly unknown[]>,
  mutationFn,
  onSuccess,
  confirmLabel,
  destructive = false,
}: ActAsMutationConfirmProps) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const canConfirm = reason.trim().length > 0 && !busy;

  async function handleConfirm() {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await mutationFn();
      // Invalidate all listed query keys so tabs re-fetch fresh data.
      await Promise.all(
        queryKeysToInvalidate.map((key) => queryClient.invalidateQueries({ queryKey: key })),
      );
      show({
        tone: 'success',
        title: t('cdn_domain.mutation_confirm.success', 'Changes saved'),
      });
      setReason('');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error)?.message ??
        t('cdn_domain.mutation_confirm.error_fallback', 'An error occurred');
      show({
        tone: 'danger',
        title: t('cdn_domain.mutation_confirm.error_title', 'Could not save changes'),
        description: msg,
      });
    } finally {
      setBusy(false);
    }
  }

  function handleOpenChange(next: boolean) {
    if (!next && busy) return; // Prevent closing while the mutation is in flight.
    if (!next) setReason('');
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogHeader>
        {title}
      </DialogHeader>

      <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* Change summary */}
        <Alert tone="info">
          <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{summary}</span>
        </Alert>

        {/* Operator reason — required to enable confirm */}
        <Field
          label={t('cdn_domain.mutation_confirm.reason_label', 'Reason for change')}
          description={t(
            'cdn_domain.mutation_confirm.reason_description',
            'Required — explain why this change is being made. Logged for audit.',
          )}
        >
          {(props) => (
            <Input
              {...props}
              value={reason}
              placeholder={t(
                'cdn_domain.mutation_confirm.reason_placeholder',
                'e.g. Customer requested lower TTL for release day',
              )}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              autoFocus
            />
          )}
        </Field>
      </DialogBody>

      <DialogFooter>
        <Button variant="secondary" disabled={busy} onClick={() => handleOpenChange(false)}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant={destructive ? 'danger' : 'primary'}
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {busy
            ? t('cdn_domain.mutation_confirm.saving', 'Saving…')
            : (confirmLabel ?? t('cdn_domain.mutation_confirm.confirm', 'Confirm'))}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
